import type { Direction, Protocol } from '@/types';

// A traffic profile emulates a real application by presetting the transport
// parameters AND the DSCP/QoS marking. The agent sets the IP TOS byte on the
// socket; APs (EERO/WMM) map its DSCP (top 6 bits) to a radio queue —
// Voice > Video > Best Effort > Background — so marking lets latency-sensitive
// or high-priority traffic avoid being starved by bulk Best-Effort flows.
//
// NOTE: `tos` is the full IP TOS byte we send = DSCP << 2. e.g. DSCP 46 (EF,
// Voice) → tos 184. The kernel/driver reads the DSCP to pick the WMM queue.
export interface TrafficProfile {
  id: string;
  name: string;
  protocol: Protocol;
  direction: Direction;
  bitrate_mbps: number; // 0 = unlimited
  packet_size?: number;
  tos: number; // IP TOS byte (DSCP << 2)
  dscp: number; // for display
  qos: string; // WMM access category label
  hint: string;
}

// Common DSCP → TOS-byte options (also used by the Custom picker).
export const DSCP_OPTIONS: { tos: number; dscp: number; label: string }[] = [
  { tos: 0, dscp: 0, label: 'Best Effort (DSCP 0)' },
  { tos: 32, dscp: 8, label: 'Background (CS1 / DSCP 8)' },
  { tos: 128, dscp: 32, label: 'Video (CS4 / DSCP 32)' },
  { tos: 136, dscp: 34, label: 'Video (AF41 / DSCP 34)' },
  { tos: 184, dscp: 46, label: 'Voice (EF / DSCP 46)' },
];

// Short label for a TOS byte, e.g. "DSCP 46 · Voice".
export function tosLabel(tos: number | undefined): string {
  const t = tos ?? 0;
  const dscp = t >> 2;
  const qos =
    dscp >= 46 ? 'Voice' : dscp >= 32 ? 'Video' : dscp >= 8 && dscp < 24 ? 'Background' : 'Best Effort';
  return `DSCP ${dscp} · ${qos}`;
}

export const CUSTOM_PROFILE = 'custom';

export const TRAFFIC_PROFILES: TrafficProfile[] = [
  {
    id: 'bulk-download',
    name: 'Bulk download',
    protocol: 'tcp',
    direction: 'down',
    bitrate_mbps: 0,
    packet_size: 16384,
    tos: 0,
    dscp: 0,
    qos: 'Best Effort',
    hint: 'Saturating TCP download — the traffic APs tend to de-prioritize.',
  },
  {
    id: 'video-stream',
    name: 'Video stream',
    protocol: 'udp',
    direction: 'down',
    bitrate_mbps: 25,
    packet_size: 1400,
    tos: 136,
    dscp: 34,
    qos: 'Video',
    hint: 'One-way HD/4K video, marked AC_VI so it rides the video queue.',
  },
  {
    id: 'video-call',
    name: 'Teams / Zoom call',
    protocol: 'udp',
    direction: 'both',
    bitrate_mbps: 3,
    packet_size: 300,
    tos: 184,
    dscp: 46,
    qos: 'Voice',
    hint: 'Interactive two-way A/V call, marked AC_VO for lowest latency.',
  },
  {
    id: 'voip',
    name: 'VoIP call',
    protocol: 'udp',
    direction: 'both',
    bitrate_mbps: 1,
    packet_size: 160,
    tos: 184,
    dscp: 46,
    qos: 'Voice',
    hint: 'Voice-only call (approx — rate is capped at the 1 Mbps minimum).',
  },
  {
    id: 'gaming',
    name: 'Online gaming',
    protocol: 'udp',
    direction: 'both',
    bitrate_mbps: 1,
    packet_size: 128,
    tos: 128,
    dscp: 32,
    qos: 'Video',
    hint: 'Small, frequent, latency-sensitive packets on the video queue.',
  },
  {
    id: '4k-stream',
    name: '4K video stream',
    protocol: 'udp',
    direction: 'down',
    bitrate_mbps: 25,
    packet_size: 1400,
    tos: 136,
    dscp: 34,
    qos: 'Video',
    hint: 'Sustained ~25 Mbps downstream — the QoE floor for premium 4K streaming.',
  },
  {
    id: 'web-browse',
    name: 'Web browsing',
    protocol: 'tcp',
    direction: 'down',
    bitrate_mbps: 8,
    packet_size: 1200,
    tos: 0,
    dscp: 0,
    qos: 'Best Effort',
    hint: 'Bursty best-effort HTTP — the "does it feel snappy" everyday load.',
  },
  {
    id: 'iot',
    name: 'IoT / smart home',
    protocol: 'udp',
    direction: 'up',
    bitrate_mbps: 1,
    packet_size: 256,
    tos: 32,
    dscp: 8,
    qos: 'Background',
    hint: 'Many small upstream packets on the background queue — sensors and telemetry.',
  },
];

export function profileById(id: string): TrafficProfile | undefined {
  return TRAFFIC_PROFILES.find((p) => p.id === id);
}
